<template>
    <div
        class="stock-edit-bar ppv2-bg-white dark:ppv2-bg-dark"
        :style="barStyle"
        role="region"
        :aria-label="$t('stock.bulk.title')"
    >
        <div class="stock-edit-bar__inner">
            <div class="stock-edit-bar__text">
                <!-- « Modifiez vos stocks » tant que rien n'a changé, puis le résumé le remplace -->
                <span
                    class="stock-edit-bar__title poppinsSemiBold"
                    :class="summary ? 'has-changes' : 'ppv2-text-black'"
                    aria-live="polite"
                >
                    {{ summary || $t('stock.bulk.title') }}
                </span>
            </div>

            <div class="stock-edit-bar__note">
                <input
                    :value="value"
                    type="text"
                    maxlength="500"
                    class="stock-edit-bar__input poppinsRegular"
                    :placeholder="$t('stock.bulk.note_placeholder')"
                    :aria-label="$t('stock.bulk.note_placeholder')"
                    :disabled="loading"
                    @input="$emit('input', $event.target.value)"
                    @keydown.enter.prevent="canSave && !loading ? $emit('save') : null"
                />
            </div>

            <div class="stock-edit-bar__actions">
                <BaseButton
                    variant="outline"
                    :custom-height="40"
                    :custom-font-size="14"
                    :disabled="loading"
                    custom-class="!ppv2-px-5"
                    @click="$emit('cancel')"
                >
                    {{ $t('stock.bulk.cancel') }}
                </BaseButton>
                <BaseButton
                    variant="purple"
                    :custom-height="40"
                    :custom-font-size="14"
                    :disabled="!canSave"
                    :loading="loading"
                    custom-class="!ppv2-px-5"
                    @click="$emit('save')"
                >
                    {{ $t('stock.bulk.save') }}
                </BaseButton>
            </div>
        </div>
    </div>
</template>

<script>
import BaseButton from '../../Shared/BaseButton.vue'

export default {
    name: 'StockEditBar',
    components: { BaseButton },
    props: {
        /** Note partagée du lot (v-model). */
        value: {
            type: String,
            default: '',
        },
        summary: {
            type: String,
            default: '',
        },
        canSave: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            barStyle: { left: '0px', width: '100%' },
        }
    },
    methods: {
        measure() {
            const main = document.getElementById('main-container')
            if (!main) {
                this.barStyle = { left: '0px', width: '100%' }
                return
            }
            const rect = main.getBoundingClientRect()
            this.barStyle = { left: `${Math.max(0, rect.left)}px`, width: `${rect.width}px` }
        },
    },
    mounted() {
        // `#page-container` est en overflow-y: hidden : la barre vit dans <body> pour rester fixe
        // quel que soit l'ancêtre transformé ou tronqué. Vue retire le nœud via son parent réel.
        document.body.appendChild(this.$el)
        this.measure()
        this._onResize = () => this.measure()
        window.addEventListener('resize', this._onResize)
        const sidebar = document.getElementById('sidebar_app')
        if (sidebar && typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(() => this.measure())
            this._resizeObserver.observe(sidebar)
        }
        // La sidebar anime sa largeur : une mesure différée rattrape la fin de la transition
        this._measureTimer = setTimeout(() => this.measure(), 350)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this._onResize)
        if (this._resizeObserver) this._resizeObserver.disconnect()
        clearTimeout(this._measureTimer)
        if (this.$el && this.$el.parentNode) this.$el.parentNode.removeChild(this.$el)
    },
}
</script>

<style scoped lang="scss">
.stock-edit-bar {
    position: fixed;
    bottom: 0;
    z-index: 60;
    box-shadow: 0 -8px 30px rgba(2, 24, 50, 0.12);
    border-top: 1px solid #e2d0f3;
    animation: stock-edit-bar-in 0.2s ease-out;
}

.stock-edit-bar__inner {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 24px;
    flex-wrap: wrap;
}

.stock-edit-bar__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 200px;
}

.stock-edit-bar__title {
    font-size: 15px;

    &.has-changes {
        color: #7d2dbd;
    }
}

.stock-edit-bar__note {
    flex: 1 1 260px;
}

.stock-edit-bar__input {
    width: 100%;
    height: 40px;
    padding: 0 14px;
    border: 1px solid #d9d2e3;
    border-radius: 12px;
    font-size: 14px;
    color: #021832;
    background: #fff;
    outline: none;
    transition: border-color 0.15s ease;

    &:focus {
        border-color: #7d2dbd;
        box-shadow: 0 0 0 3px rgba(125, 45, 189, 0.12);
    }

    &::placeholder {
        color: #9ca3af;
    }
}

.stock-edit-bar__actions {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
}

.dark .stock-edit-bar__input {
    background: #1a1f37;
    color: #fff;
    border-color: #3a3f5c;
}

@keyframes stock-edit-bar-in {
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0);
    }
}

@media (max-width: 767px) {
    .stock-edit-bar__inner {
        padding: 12px 16px;
    }

    .stock-edit-bar__actions {
        width: 100%;
        justify-content: flex-end;
        margin-left: 0;
    }
}
</style>
